import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { useToasts } from '../Toasts';
import API from '../../Services/API';
import { ulid } from 'ulid';
import { usePreference } from '../../Data/Preference';
import { useSocketListener, useSocketRecipient } from '../Socket';
import Actions from './Actions';
import { useRedirect } from '../../Services/helpers';
import { useLocation } from 'react-router-dom';
import { useSession } from '../Session';
import { useMobile } from '../Mobile';
import { captureException } from '../../Services/Sentry';

const AssistantContext = createContext();

export const useAssistantContext = () => {
    return useContext(AssistantContext);
};

const recognitionAvailable = !!(window.webkitSpeechRecognition || window.SpeechRecognition);
let recognition;
if (recognitionAvailable) {
    recognition = window.webkitSpeechRecognition ?
        new window.webkitSpeechRecognition() :
        new window.SpeechRecognition();
    recognition.lang = "en-GB";
    recognition.interimResults = true;
}

const styleFloating = 'floating';
const styleModal = 'modal';

const socketTopic = 'assistant';

let actions;

export const Provider = ({ children }) => {
    const {isReady, isAdmin, user, business} = useSession();
    const {isMobile, isVersionAtLeast} = useMobile();
    const {showError} = useToasts();
    const {Redirect, go} = useRedirect();
    const currentPath = useLocation()?.pathname;
    const [assistantOpen, setAssistantOpen] = usePreference('assistant:open', false);
    const [mobileStyle, setMobileStyle] = usePreference('assistant:mobileStyle', styleFloating);
    const [welcomeMessage, setWelcomeMessage] = useState(null);
    const conversationId = useRef(null);
    const [conversation, setConversation] = useState(null);
    const [showAssets, setShowAssets] = useState(false);
    const [assetIds, setAssetIds] = useState([]);
    const [entries, setEntries] = useState([]);
    const [files, setFiles] = useState([]);
    const [dictating, setDictating] = useState(false);
    const [tmpQuestion, setTmpQuestion] = useState('');
    const [sendingQuestion, setSendingQuestion] = useState(null);
    const [assistantTyping, setAssistantTyping] = useState(false);
    const [streamingAnswers, setStreamingAnswers] = useState(['']);
    const [activeToolTitles, setActiveToolTitles] = useState(null);
    const [previousConversations, setPreviousConversations] = useState([]);
    const [unseen, setUnseen] = useState(false);
    const dictationAvailable = recognitionAvailable && (!isMobile || (isAdmin() && isVersionAtLeast('2.1.0')));

    const loading = useRef(false);
    const markingSeen = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    if (!actions) {
        actions = new Actions((r) => go(r));
    }

    const unlessLoading = (fn) => {
        if (!isReady) {
            return;
        }
        if (loading.current) {
            return;
        }

        loading.current = true;
        setIsLoading(true);

        return fn().finally(() => {
            loading.current = false
            setIsLoading(false);
        });
    }

    const refreshPreviousConversations = () => {
        API.get('/assistant/previous').then(setPreviousConversations);
    }

    const checkForNudges = () => {
        return API.get('/assistant/nudges');
    }

    const newConversation = (humanAttention = false, trigger = 'UserStarted', welcomeMessage = true, nudgeKey = null) => {
        conversationId.current = null;
        setConversation(null);
        setSendingQuestion(null);
        setEntries([]);
        setFiles([]);
        setStreamingAnswers(['']);
        setAssistantTyping(true);
        startConversation(humanAttention, trigger, welcomeMessage, nudgeKey);
    }

    const startConversation = (humanAttention = false, trigger = 'UserStarted', welcomeMessage = true, nudgeKey = null) => {
        unlessLoading(() => {
            const _welcomeMessage = welcomeMessage ? pickWelcomeMessage() : null;
            setWelcomeMessage(_welcomeMessage);
            const newConversationId = ulid();
            conversationId.current = newConversationId;
            setAssistantTyping(true);

            const requestData = {
                socketRecipient,
                currentPath,
                trigger,
                humanAttention,
                welcomeMessage: _welcomeMessage,
            };

            if (nudgeKey) {
                requestData.nudge = nudgeKey;
            }

            return API.post(
                `/assistant/${newConversationId}`,
                requestData
            ).then(conversation => {
                saveConversation(conversation, false);
            }).catch(e => {
                conversationId.current = null;
                showError('Failed to start conversation with assistant');
                console.error(e);
                captureException(e);
            });
        });
    }

    const pickWelcomeMessage = () => {
        const options = [
            `Hey ${user.firstName}! How can I assist you today with getSoapy?`,
            `Hey ${user.firstName}! How can I help you today with ${business.name}?`,
            `Hi ${user.firstName}! What can I do for you today to make ${business.name} run smoother?`,
            `Welcome back ${user.firstName}! How can I support you today?`,
            `Hello! I'm ready to tackle any questions or tasks you have. What's on your mind?`,
            `Good to see you ${user.firstName}! What would you like to achieve with getSoapy today?`,
            `Hello! Let's make your day easier. How can I assist you with for ${business.name}?`,
        ]

        return options[Math.floor(Math.random() * options.length)];
    }

    const requestHumanAttention = () => {
        unlessLoading(() => {
            return API.post(`/assistant/${conversationId.current}/humanAttention`).then(() => {
                refreshConversation();
            });
        });
    }

    const nudgeAssistant = () => {
        unlessLoading(() => {
            return API.post(`/assistant/${conversationId.current}/nudge`).then(() => {
                refreshConversation();
            });
        });
    }

    const restoreConversation = () => {
        if (conversationId.current) {
            return;
        }

        unlessLoading(() => {
            return API.get(`/assistant/started`).then(conversation => {
                if (conversation.id) {
                    saveConversation(conversation, false);
                } else {
                    conversationId.current = null;
                    setConversation(null);
                    setEntries([]);
                    setFiles([]);

                    // Check for nudges first, regardless of assistantOpen state
                    return checkForNudges().then(nudges => {
                        if (nudges?.length > 0) {
                            // Start conversation automatically with the first nudge
                            const nudge = nudges[0];
                            // Do this once the unlessLoading has finished
                            setTimeout(
                                () => startConversation(false, 'Nudge', false, nudge.nudgeKey),
                                1000,
                            );
                        } else if (assistantOpen) {
                            // We need to wait until unlessLoading has finished before starting the conversation
                            // because it sets isLoading to true
                            setTimeout(() => startConversation(), 100);
                            setTimeout(() => startConversation(), 500);
                        }
                    }).catch(e => {
                        console.error('Failed to check for nudges:', e);
                        captureException(e);

                        // Fall back to normal behaviour if nudge check fails
                        if (assistantOpen) {
                            setTimeout(() => startConversation(), 100);
                            setTimeout(() => startConversation(), 500);
                        }
                    });
                }
            });
        });
    }

    const refreshConversation = useCallback(() => {
        if (!conversationId.current) {
            return;
        }

        unlessLoading(() => {
            return API.get(`/assistant/${conversationId.current}`).then(conversation => {
                saveConversation(conversation, true);
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId.current]);

    const submitMessage = (message) => {
        unlessLoading(() => {
            setTmpQuestion('');
            setSendingQuestion(message);
            return API.post(`/assistant/${conversationId.current}/messages`, {message, socketRecipient, currentPath, assetIds}).then(conversation => {
                saveConversation(conversation, true);
                setShowAssets(false);
                setAssetIds([]);
            }).catch(e => {
                showError('Failed to send message to assistant');
                clearStreaming();
                setTmpQuestion(message.trim());
                console.error(e);
                captureException(e);
            });
        });
    }

    const markAsSeen = () => {
        if (markingSeen.current) {
            return;
        }
        markingSeen.current = true;
        setUnseen(0);
        API.post(`/assistant/${conversationId.current}/seen`).catch(e => {
            console.error(e);
            captureException(e);
        }).finally(() => {
            setUnseen(0);
            markingSeen.current = false;
        });
    }

    const saveConversation = (conversation, performActions) => {
        actions.handleActions(conversation, performActions);

        if (conversation.status === 'Archived') {
            conversationId.current = null;
            setConversation(null);
            setEntries([]);
            setFiles([]);
            setUnseen(0);
            return;
        }

        conversationId.current = conversation.id;
        setConversation(conversation);
        setUnseen(conversation.unseenByUser ?? 0);

        if (conversation.entries) {
            setEntries(existing => {
                const newEntries = conversation.entries.filter(
                    newEntry => !existing.some(existingEntry => existingEntry.id === newEntry.id)
                );
                return [...existing, ...newEntries];
            });
            setFiles(conversation.files ?? []);
            clearStreaming();
        }
    }

    const clearStreaming = () => {
        setStreamingAnswers(['']);
        setSendingQuestion(null);
        setActiveToolTitles([]);
        setAssistantTyping(false);
    }

    const startDictating = () => {
        setDictating(true);
        recognition.start();
    }

    const stopDictating = () => {
        recognition.stop();
        setDictating(false);
        if (tmpQuestion?.trim()) {
            submitMessage(tmpQuestion.trim());
        }
    }

    const toggleMobileStyle = () => setMobileStyle(mobileStyle === styleFloating ? styleModal : styleFloating);

    const toggleDictation = () => dictating ? stopDictating() : startDictating();

    // const debouncedAgentTyped = useDebounceCallback(() => refreshConversation(), 2000);

    useEffect(() => {
        if (!conversation) {
            return;
        }
        refreshPreviousConversations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation?.id]);

    // We need a second listener for admin messages because they don't come through the assistant topic
    useSocketListener(
        {entity: {entityModel: 'Assistant/Conversation', entityId: conversationId.current}},
        (msg) => {
            if (msg.event === 'ConversationWasReviewed') {
                refreshPreviousConversations();
                return;
            }

            const ignore = [
                'ConversationWasMarkedAsSeen',
                'ConversationWasFinished',
                'ConversationWasArchived',
            ];
            if (ignore.includes(msg.event)) {
                return;
            }

            // If we get an ID which doesn't match, it's probably an archived conversation
            if (msg.conversation && msg.conversation.id !== conversationId.current) {
                return
            }

            if (msg.eventData && msg.eventData?.role === 'Admin') {
                setEntries(existing => {
                    if (existing.some(entry => entry.message === msg.eventData.message && entry.role === 'Admin')) {
                        return existing;
                    }
                    return [
                        ...existing,
                        {id: msg.eventData.messageId, role: 'Admin', message: msg.eventData.message}
                    ];
                });
            }
            if (msg.conversation) {
                saveConversation(msg.conversation, true);
            }
        }
    );

    // These are only used for streaming answers
    useSocketListener({topic: socketTopic}, (msg) => {
        if (msg.event === 'ConversationWasStarted' && msg.conversation) {
            saveConversation(msg.conversation, false);
            return;
        }

        if (msg.assistantTyping === true) {
            setAssistantTyping(true);
        }
        if (msg.assistantTyping === false) {
            setAssistantTyping(false);
            setTimeout(() => {
                refreshConversation();
            }, 100);
        }
        if (msg.chunk) {
            setStreamingAnswers(existing => {
                const last = existing[existing.length - 1];
                const updated = last.endsWith(msg.chunk) ? last : last + msg.chunk;
                return [...existing.slice(0, -1), updated];
            });
            setAssistantTyping(false);
            setActiveToolTitles([]);
            // debouncedAgentTyped();
        }
        if (msg.toolTitle) {
            // setActiveToolTitles(existing => {
            //     if (!existing) {
            //         return [msg.toolTitle];
            //     }
            //     if (existing.includes(msg.toolTitle)) {
            //         return existing;
            //     }
            //     return [...existing, msg.toolTitle];
            // });
            setActiveToolTitles([msg.toolTitle]);
            setStreamingAnswers(existing => [...existing, '']);
            setAssistantTyping(false);
        }
    });
    const socketRecipient = useSocketRecipient(socketTopic);

    useEffect(() => {
        if (!dictationAvailable) {
            return;
        }

        const onResult = (event) => setTmpQuestion(event.results[0][0].transcript);
        const onSpeechEnd = () => stopDictating();

        const onError = (event) => {
            console.error(event);
            setDictating(false);
            recognition.stop();
            showError(`Speech recognition error detected: ${event.error}`)
        }

        recognition.addEventListener('result', onResult);
        recognition.addEventListener('speechend', onSpeechEnd);
        recognition.addEventListener('error', onError);

        return () => {
            recognition.removeEventListener('result', onResult);
            recognition.removeEventListener('speechend', onSpeechEnd);
            recognition.removeEventListener('error', onError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loading.current) {
            return;
        }

        if (!assistantOpen && conversationId.current) {
            return;
        }

        if (!conversationId.current) {
            restoreConversation();
            return;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, assistantOpen, conversationId.current, conversation]);

    useEffect(() => {
        if (assistantOpen && unseen > 0 && !markingSeen.current) {
            markAsSeen();
        }
    }, [unseen, assistantOpen]);

    useEffect(() => {
        if (assistantOpen && conversationId.current) {
            refreshConversation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantOpen]);

    const providerValues = {
        assistantOpen: isReady && assistantOpen,
        mobileStyle,
        toggleMobileStyle,
        welcomeMessage,
        conversation,
        entries,
        files,
        recognitionAvailable,
        dictationAvailable,
        dictating,
        showAssets,
        setShowAssets,
        assetIds,
        setAssetIds,
        setAssistantOpen,
        toggleDictation,
        startDictating,
        stopDictating,
        tmpQuestion,
        setTmpQuestion,
        sendingQuestion,
        submitMessage,
        newConversation,
        requestHumanAttention,
        nudgeAssistant,
        assistantTyping,
        streamingAnswers,
        activeToolTitles,
        isLoading,
        unseen,
        previousConversations,
        refreshPreviousConversations,
    };

    return (
        <AssistantContext.Provider value={providerValues}>
            {children}
            <Redirect />
        </AssistantContext.Provider>
    );
};
